import { ControlledInputConfig } from '@/components/customByConfiguration';
import { QueryKey } from '@/enums';
import { useFetchRoles, useValidationForm } from '@/hooks';
import { UserService } from '@/services';
import { CustomError, errorNotification } from '@/utils';
import { assignRoleSchema } from '@/validations';
import { CustomButton } from '@amili-v3/amili-v3-design';
import { Form, notification } from 'antd';
import { Formik } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminFormWrapper } from './styles';
import { useGetRoleOptions } from './utils';

const ASSIGN_ROLE_FORM = 'assign-role-form';
const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  roles: '',
};

const AssignRoleForm = (props: {
  assignRole: string;
  setAssignRole: Dispatch<SetStateAction<string>>;
}) => {
  const { assignRole, setAssignRole } = props;
  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const { isLoading, data: allRoles } = useFetchRoles();

  const mutationAssign = useMutation({
    mutationFn: UserService.assignRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ADMINISTRATOR.ALL] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROFILE.ME] });
      notification.success({ message: t('common.success') });
      setAssignRole('');
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const handleSubmit = (values: Record<string, any>) => {
    return mutationAssign.mutateAsync({
      id: assignRole,
      roles: [values.roles],
    });
  };

  const adminOptions = useGetRoleOptions(allRoles);
  const ConfigSchema = useValidationForm(ASSIGN_ROLE_FORM, assignRoleSchema);

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={ConfigSchema}
    >
      {({ handleSubmit: handleFinish }) => {
        return (
          <AdminFormWrapper>
            <Form
              onFinish={handleFinish}
              layout="vertical"
              id={ASSIGN_ROLE_FORM}
            >
              <ControlledInputConfig
                required
                isPreventCustomConfig
                name="roles"
                aria-label="roles"
                inputType="select"
                loading={isLoading}
                placeholder="Select"
                options={adminOptions}
                label={t('common.roles')}
              />

              <CustomButton
                width="100%"
                size="large"
                key="create"
                htmlType="submit"
                loading={mutationAssign.isPending}
              >
                {t('common.assign')}
              </CustomButton>
            </Form>
          </AdminFormWrapper>
        );
      }}
    </Formik>
  );
};

export default AssignRoleForm;
